import React, { useCallback, useMemo, useState } from 'react'
import {
  REGIONY, REGIONY_RYSOWANIE, VIEW_BOX, TLO_D, Region, KluczRegionu, LicznikRegionu,
} from './regionyPolski'
import './MapaPolski.css'

/**
 * Mapa Polski — 16 województw, każde osobną ścieżką SVG.
 *
 * PODZIAŁ ODPOWIEDZIALNOŚCI: komponent nie wie nic o wykonawcach ani sklepach.
 * Dostaje `wybrany`, oddaje `onWybierz(key)` i opcjonalnie pyta o licznik. Ta sama
 * mapa filtruje później cokolwiek innego bez zmiany w jej kodzie.
 *
 * ══ DLACZEGO KOLEJNOŚĆ W DOM JEST STAŁA ══
 * Wcześniej aktywne województwo było przesuwane na koniec listy, żeby jego cień
 * kładł się na sąsiadach (w SVG nie ma `z-index`). To znaczyło, że KAŻDE najechanie
 * kursorem przestawiało węzeł w DOM. Przestawienie węzła pomiędzy `mousedown`
 * a `mouseup` gubi zdarzenie `click` — przeglądarka wysyła je tylko wtedy, gdy oba
 * wypadły na TYM SAMYM elemencie. Stąd wrażenie, że województwo przełącza się
 * dopiero za drugim kliknięciem.
 *
 * Teraz warstwa klikalna ma NIEZMIENNĄ kolejność, a cień rysuje osobna warstwa
 * dekoracyjna na wierzchu, z `pointer-events: none`. Efekt wizualny ten sam,
 * a w DOM przy najechaniu zmienia się wyłącznie `class` i `fill`.
 *
 * DLACZEGO NAZWY NIE STOJĄ NA MAPIE: przy szesnastu obszarach „Zachodniopomorskie"
 * czy „Warmińsko-mazurskie" nie mieszczą się w konturze, a opolskie i śląskie są
 * na to za wąskie. Nazwa pojawia się pod mapą, a wewnątrz zostaje sama liczba —
 * i tylko tam, gdzie jest na nią miejsce.
 *
 * ANIMACJE SĄ W CSS: chodzą na kompozytorze i obejmują `:focus-visible`,
 * którego stan Reacta by nie objął.
 */

export interface MapaPolskiProps {
  wybrany: KluczRegionu | null
  /** Klik w JUŻ wybrane województwo oddaje `null` — czyli zwalnia wybór. */
  onWybierz: (key: KluczRegionu | null) => void
  licznik?: LicznikRegionu
  /** Odmiana rzeczownika przez liczbę — polski ma trzy formy, nie dwie. */
  licznikPodpis?: (n: number) => string
  className?: string
  /**
   * TRYB WIELOKROTNY (formularz wykonawcy: firma pracuje w kilku województwach).
   * Gdy podany, `wybrany` jest ignorowany, a zaznaczenie niesie ta lista.
   * Wybór użytkownika na ekranie doboru zostaje pojedynczy — dlatego to osobny,
   * opcjonalny prop, a nie zmiana istniejącego zachowania.
   */
  wybrane?: KluczRegionu[]
  /**
   * Treść rysowana NA województwie (np. znaki operatorów sieci w narzędziu zgłoszeń OSD).
   * Warstwa bez obsługi zdarzeń, więc nie zmienia trafień kliknięć. Współrzędne w jednostkach VIEW_BOX.
   */
  nakladka?: (r: Region) => React.ReactNode
  /** Dopisek pod mapą obok nazwy opisywanego województwa (zamiast licznika). */
  dopisek?: (key: KluczRegionu) => React.ReactNode
  /** Zachęta pod mapą, gdy nic nie jest opisywane. */
  zacheta?: string
}

/** Poniżej tego pola licznik nie mieści się w konturze (opolskie ≈ 6900). */
const POLE_NA_LICZNIK = 8000

interface WojewodztwoProps {
  region: Region
  wybrany: boolean
  najechany: boolean
  liczba: number | null
  podpis: string
  onWejscie: (key: KluczRegionu) => void
  onWyjscie: () => void
  onKlik: (key: KluczRegionu, wybrany: boolean) => void
}

/**
 * Pojedyncze województwo. `React.memo` ma tu realne znaczenie: przy przesunięciu
 * kursora zmienia się stan DWÓCH województw — opuszczanego i wskazywanego —
 * a pozostałe czternaście dostaje identyczne właściwości i nie przerysowuje się.
 */
const Wojewodztwo = React.memo<WojewodztwoProps>(({
  region: r, wybrany, najechany, liczba, podpis, onWejscie, onWyjscie, onKlik,
}) => (
  <g
    className={`vv-mapa__region${wybrany ? ' vv-mapa__region--wybrany' : ''}${najechany ? ' vv-mapa__region--najechany' : ''}`}
    onMouseEnter={() => onWejscie(r.key)}
    onMouseLeave={onWyjscie}
    onFocus={() => onWejscie(r.key)}
    onBlur={onWyjscie}
    onClick={() => onKlik(r.key, wybrany)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onKlik(r.key, wybrany) }
    }}
    tabIndex={0}
    role="button"
    aria-pressed={wybrany}
    aria-label={`${r.nazwa}${liczba != null ? `: ${liczba} ${podpis}` : ''}`}
  >
    {/* Natywna podpowiedź przeglądarki — nazwa dostępna bez klikania. */}
    <title>{r.nazwa}{liczba != null ? ` — ${liczba} ${podpis}` : ''}</title>
    <path
      d={r.d}
      className="vv-mapa__ksztalt"
      fill={wybrany ? 'url(#vvGradWybrany)' : najechany ? 'url(#vvGradNajechany)' : 'rgb(var(--vv-mapa-region))'}
    />
    {liczba != null && liczba > 0 && r.pole >= POLE_NA_LICZNIK && (
      <text x={r.etykieta.x} y={r.etykieta.y + 5} className="vv-mapa__licznik" textAnchor="middle">
        {liczba}
      </text>
    )}
  </g>
))
Wojewodztwo.displayName = 'Wojewodztwo'

const MapaPolski: React.FC<MapaPolskiProps> = ({
  wybrany, onWybierz, licznik, licznikPodpis = () => '', className = '', wybrane, nakladka, dopisek, zacheta,
}) => {
  const [najechany, setNajechany] = useState<KluczRegionu | null>(null)
  const trybWielokrotny = Array.isArray(wybrane)
  const czyZaznaczony = (key: KluczRegionu) =>
    trybWielokrotny ? wybrane!.includes(key) : wybrany === key

  // Stałe referencje — bez nich `React.memo` na województwach nic nie daje,
  // bo każdy render podawałby im nowe funkcje.
  const onWejscie = useCallback((key: KluczRegionu) => setNajechany(key), [])
  const onWyjscie = useCallback(() => setNajechany(null), [])
  const onKlik = useCallback(
    (key: KluczRegionu, jestWybrany: boolean) => onWybierz(jestWybrany ? null : key),
    [onWybierz]
  )

  // Liczniki wyliczane RAZ na zmianę źródła, a nie przy każdym ruchu kursora.
  const liczby = useMemo(
    () => new Map(REGIONY.map((r) => [r.key, licznik ? licznik(r.key) : null])),
    [licznik]
  )

  const opisywany = najechany ?? (trybWielokrotny ? null : wybrany)
  const opisRegion = REGIONY.find((r) => r.key === opisywany) ?? null
  const opisLiczba = opisRegion ? liczby.get(opisRegion.key) ?? null : null

  // Warstwa dekoracyjna: kontur aktywnego województwa powtórzony na wierzchu,
  // żeby cień padał na sąsiadów. Nieklikalna, więc nie miesza w trafieniach.
  // W trybie wielokrotnym cień chodzi TYLKO za kursorem — przy kilku zaznaczonych
  // województwach cień pod każdym z nich zlewa mapę w jedną plamę.
  const naWierzchu = REGIONY.find((r) => r.key === (najechany ?? (trybWielokrotny ? null : wybrany))) ?? null
  const naWierzchuWybrany = naWierzchu != null && czyZaznaczony(naWierzchu.key)

  return (
    <div className={`vv-mapa ${className}`}>
      <svg viewBox={VIEW_BOX} className="vv-mapa__svg" role="group" aria-label="Wybór województwa">
        <defs>
          {/* Cień niebieskawy, nie czarny: czarny na jasnoszarym czyta się jak brud. */}
          <filter id="vvCienRegionu" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="6" stdDeviation="7" floodColor="#1d4ed8" floodOpacity="0.32" />
          </filter>
          {/* Dwa odcienie, bo oba stany mogą wystąpić JEDNOCZEŚNIE: jedno województwo
              jest wybrane, a kursor stoi nad innym. W jednym kolorze wyglądało to,
              jakby poprzedni wybór się zablokował. */}
          <linearGradient id="vvGradWybrany" x1="0" y1="0" x2="0.35" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#1d4ed8" />
          </linearGradient>
          <linearGradient id="vvGradNajechany" x1="0" y1="0" x2="0.35" y2="1">
            <stop offset="0%" stopColor="#bfdbfe" />
            <stop offset="100%" stopColor="#93c5fd" />
          </linearGradient>
        </defs>

        {/* Podkład ze WSZYSTKICH konturów, POSZERZONY własnym obrysem.
            Upraszczanie granic robione jest dla każdego województwa osobno, więc na
            stykach zostają włosowe szczeliny (zmierzone: 7 punktów na 27 000).
            Samo złożenie ścieżek ich nie zamyka — ma dokładnie te same dziury.
            Dopiero obrys w tym samym szarym rozszerza podkład o 1,5 jednostki. */}
        <path d={TLO_D} fill="rgb(var(--vv-mapa-region))" stroke="rgb(var(--vv-mapa-region))" strokeWidth={3} strokeLinejoin="round" />

        {/* Warstwa klikalna — kolejność NIGDY się nie zmienia. */}
        {REGIONY_RYSOWANIE.map((r) => (
          <Wojewodztwo
            key={r.key}
            region={r}
            wybrany={czyZaznaczony(r.key)}
            najechany={najechany === r.key && !czyZaznaczony(r.key)}
            liczba={liczby.get(r.key) ?? null}
            podpis={licznikPodpis(liczby.get(r.key) ?? 0)}
            onWejscie={onWejscie}
            onWyjscie={onWyjscie}
            onKlik={onKlik}
          />
        ))}

        {/* Warstwa dekoracyjna — sam cień, bez obsługi zdarzeń. */}
        {naWierzchu && (
          <g
            className={`vv-mapa__cien${naWierzchuWybrany ? ' vv-mapa__region--wybrany' : ' vv-mapa__region--najechany'}`}
            filter="url(#vvCienRegionu)"
            aria-hidden="true"
          >
            <path
              d={naWierzchu.d}
              className="vv-mapa__ksztalt"
              fill={naWierzchuWybrany ? 'url(#vvGradWybrany)' : 'url(#vvGradNajechany)'}
            />
          </g>
        )}

        {/* Nakładka z treścią na województwach: NA SAMYM WIERZCHU, nad warstwą cienia, bo cień rysuje
            kształt województwa jeszcze raz i zakrywał np. logotypy operatorów po najechaniu. Bez zdarzeń. */}
        {nakladka && (
          <g className="vv-mapa__nakladka" pointerEvents="none" aria-hidden="true">
            {REGIONY.map((r) => <React.Fragment key={r.key}>{nakladka(r)}</React.Fragment>)}
          </g>
        )}
      </svg>

      {/* Stała wysokość podpisu, żeby mapa nie skakała przy przesuwaniu kursora. */}
      <p className="vv-mapa__podpis">
        {opisRegion ? (
          <>
            <span className="vv-mapa__podpis-nazwa">{opisRegion.nazwa}</span>
            {opisLiczba != null && (
              <span className="vv-mapa__podpis-licznik"> · {opisLiczba} {licznikPodpis(opisLiczba)}</span>
            )}
            {dopisek && <span className="vv-mapa__podpis-licznik"> · {dopisek(opisRegion.key)}</span>}
          </>
        ) : (
          <span className="vv-mapa__podpis-zacheta">
            {zacheta || (trybWielokrotny
              ? 'Klikaj województwa, w których pracujecie'
              : 'Najedź na województwo, żeby zobaczyć nazwę')}
          </span>
        )}
      </p>

      {!trybWielokrotny && wybrany && (
        <p className="vv-mapa__zmiana">Kliknij inne województwo, żeby zmienić wybór</p>
      )}
    </div>
  )
}

export default MapaPolski
